import App from '@/App.vue'
import BaseButton from '@/components/BaseButton.vue'
import IconButton from '@/components/IconButton.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import '@/components/Navigation/captego-nav.js'
import Sidepanel from '@/components/Sidepanel.vue'
import DEFAULT_TIMEZONE from '@/constants/defaultTimeZone'
import { firebaseApp } from '@/firebase'
import { apolloClient } from '@/gql/authenticatedClient'
import i18n from '@/i18n'
import router from '@/router'
import setupSentry from '@/sentry'
import store from '@/store'
import {
  deleteLogoutCookie,
  deleteTokenCookie,
  getLogoutCookie,
  getTokenCookie,
  setLogoutCookie,
  setTokenCookie,
} from '@/utils/cookies'
import { decodeJwtPayload, isTokenNearExpiry } from '@/utils/customToken'
import { formatDate, timeAgo, truncate } from '@/utils/filters'
import { getImgProxyUrl } from '@/utils/getImgProxyUrl'
import { isCaptegoEmail } from '@/utils/isCaptegoEmail'
import { redactIDFromURL } from '@/utils/redactIDFromURL'
import { redirectToManage } from '@/utils/redirectToManage'

import * as Sentry from '@sentry/vue'
import firebase from 'firebase/app'
import FloatingVue from 'floating-vue'
import Plausible from 'plausible-tracker'
import VTooltip from 'v-tooltip'
import Vue from 'vue'
import Affix from 'vue-affix'
import VueCookies from 'vue-cookies'
import VuePhoneNumberInput from 'vue-phone-number-input'

import 'floating-vue/dist/style.css'
import 'vue-phone-number-input/dist/vue-phone-number-input.css'

if (import.meta.env.VUE_APP_MAINTENANCE === 'true') {
  redirectToManage()
}

setupSentry(router)

const { trackEvent, trackPageview } = Plausible({
  domain: window.location.hostname,
})

Vue.use(Affix)
FloatingVue.options.distance = 8
Vue.use(FloatingVue, {
  themes: {
    form: {
      $extend: 'dropdown',
      placement: 'bottom-start',
    },
    status: {
      $extend: 'dropdown',
      placement: 'bottom-start',
    },
  },
})
Vue.use(VueCookies, {
  expires: '999d',
  path: '/',
  domain: '.captego.com',
  secure: true,
  sameSite: 'Strict',
})
Vue.use(VTooltip)

Vue.component('VuePhoneNumberInput', VuePhoneNumberInput)

Vue.config.productionTip = false

Vue.component('Sidepanel', Sidepanel)
Vue.component('IconButton', IconButton)
Vue.component('BaseButton', BaseButton)
Vue.component('LoadingSpinner', LoadingSpinner)

Vue.mixin({
  data: function () {
    return {}
  },
  computed: {
    currentUser() {
      return this.$store.getters.user
    },
    currentCompany() {
      return this.$store.getters.company
    },
    isCaptegoEmail() {
      return isCaptegoEmail(this.currentUser.email)
    },
    isNonAdmin() {
      return (
        this.currentUser.adminRole !== 'admin' &&
        this.currentUser.adminRole !== 'super_admin'
      )
    },
    isAdminOrSuperAdmin() {
      return ['admin', 'super_admin'].includes(this.currentUser.adminRole)
    },
    isUser() {
      return this.currentUser.adminRole === 'user'
    },
    isAdmin() {
      return this.currentUser.adminRole === 'admin'
    },
    isSuperAdmin() {
      return this.currentUser.adminRole === 'super_admin'
    },
    hasAdminRole() {
      return ['super_admin', 'admin', 'user'].includes(
        this.currentUser.adminRole
      )
    },
    timeZone() {
      return this.$store.getters.company.timeZone || DEFAULT_TIMEZONE
    },
    isProduction() {
      return import.meta.env.VUE_APP_PROJECT_ID === 'captego-prod-eu'
    },
  },
  methods: {
    getImgProxyUrl(url, props) {
      return getImgProxyUrl(url, props)
    },
    featureLimit(featureId, count) {
      return this.$store.getters.featureLimit(featureId, count)
    },
    hasFeature(featureId) {
      return (
        this.$store.getters.featureLimit(featureId) !== undefined &&
        this.$store.getters.featureLimit(featureId).limitation > 0
      )
    },
    trackEvent(payload) {
      const { category, action, ...eventParams } = payload
      const eventName = [category, action]
        .join('_')
        .toLowerCase()
        .replace(/\s/gi, '_')

      trackEvent(eventName, {
        props: eventParams,
      })
    },
    addPanel: function (panelID, payload) {
      this.$store.dispatch('addPanel', {
        id: panelID,
        payload,
      })
    },
    removeAllPanels: function () {
      this.$store.dispatch('removeAllPanels')
    },
    removePanel: function (panelId) {
      this.$store.dispatch('removePanel', { panelId })
    },
    isCreator(item) {
      if (!item) return false
      return item.userId === this.currentUser.uid
    },
    isOwner(item) {
      if (!item) return false
      return item.permissions?.[this.currentUser.uid]?.role === 'owner'
    },
    hasOwnerRights(item) {
      if (!item) return false
      return (
        this.isOwner(item) ||
        (item.companyId === this.currentUser.companyId &&
          this.isAdminOrSuperAdmin)
      )
    },
    isAssigneeOnly(item) {
      if (!item) return false
      const userPermissions = item.permissions
        ? item.permissions[this.currentUser.uid]
        : ''
      const userRole = userPermissions ? userPermissions.role : ''
      return userRole === 'assignee' && !this.hasOwnerRights(item)
    },
    isContributor(item) {
      if (!item) return false
      const userPermissions = item.permissions
        ? item.permissions[this.currentUser.uid]
        : ''
      if (
        this.currentUser.adminRole !== 'admin' &&
        this.currentUser.adminRole !== 'super_admin' &&
        userPermissions &&
        userPermissions.role === 'contributor'
      )
        return true
      return false
    },
    getTimeZone(item) {
      if (item?.localisation?.timeConfig?.timeZone) {
        return item.localisation.timeConfig.timeZone
      } else if (this.currentCompany?.timeZone) {
        return this.currentCompany?.timeZone
      }
      return DEFAULT_TIMEZONE
    },
    formatDate,
    timeAgo,
    truncate,
  },
})

function getAccessToken() {
  let accessToken

  try {
    const params = new URLSearchParams(window.location.search)
    accessToken = params.get('accessToken')
  } catch (err) {
    // no need to log this error
  }

  // accessToken searchParam takes precedence, token cookie from Manage comes after
  if (accessToken == null) {
    accessToken = getTokenCookie()
  }

  return accessToken
}

const refreshTokenInterval = {
  intervalId: 0,
}
function stopRefreshingIdToken() {
  if (refreshTokenInterval.intervalId) {
    clearInterval(refreshTokenInterval.intervalId)
  }
}
let refreshTokenCookieInFlight = null
function refreshCustomTokenCookie() {
  // Don't resurrect the token cookie if the user signed out from another app.
  if (getLogoutCookie()) return Promise.resolve(false)
  const uid = firebaseApp.auth().currentUser?.uid
  if (!uid) return Promise.resolve(false)
  if (refreshTokenCookieInFlight) return refreshTokenCookieInFlight

  refreshTokenCookieInFlight = apolloClient
    .refreshCustomToken()
    .then((response) => {
      // Avoid re-setting the cookie if the user signed out or switched accounts
      // while the refresh request was in flight.
      if (firebaseApp.auth().currentUser?.uid !== uid) return false
      if (response?.refreshCustomToken?.customToken) {
        setTokenCookie(response.refreshCustomToken.customToken)
        return true
      }
      return false
    })
    .catch((err) => {
      console.log('Failed to refresh custom token cookie', err)
      return false
    })
    .finally(() => {
      refreshTokenCookieInFlight = null
    })
  return refreshTokenCookieInFlight
}

function refreshIdToken(_firebaseApp) {
  stopRefreshingIdToken()
  const intervalInMilliseconds = 58 * 60 * 1000 // 58 minutes
  refreshTokenInterval.intervalId = window.setInterval(async function () {
    const refreshed = await refreshCustomTokenCookie()
    if (!refreshed && _firebaseApp.auth().currentUser) {
      // Retry quickly so a transient failure doesn't leave the cookie stale
      // until the next interval, long after the one-hour token has expired.
      window.setTimeout(refreshCustomTokenCookie, 30 * 1000)
    }
  }, intervalInMilliseconds)
}

// Interval timers are throttled in background tabs and paused while the
// machine sleeps, so the cookie can already be stale when the user returns.
// Repair it as soon as the tab becomes visible again.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState !== 'visible') return
  if (isTokenNearExpiry(getTokenCookie())) {
    refreshCustomTokenCookie()
  }
})

// Resolved once Firebase has restored (or ruled out) a persisted session, so
// the router guard doesn't mistake "still restoring" for "signed out".
let resolveInitialAuthState
const initialAuthState = new Promise((resolve) => {
  resolveInitialAuthState = resolve
})

let firstAuth = true

firebaseApp.auth().onAuthStateChanged((user) => {
  resolveInitialAuthState()
  store
    .dispatch('authChange', user)
    .then((success) => {
      if (!success) {
        const wasFirstAuth = firstAuth
        firstAuth = false
        // A signed-in user without an admin user doc doesn't belong here, and
        // a mid-session auth loss must clean up. Only the initial "nobody is
        // signed in" event passes through silently.
        if (user || !wasFirstAuth) {
          Sentry.configureScope((scope) => scope.setUser(null))
          stopRefreshingIdToken()
          deleteTokenCookie()
          firebaseApp.auth().signOut()
        }
        return
      }
      firstAuth = false

      const accessToken = getAccessToken()
      const userData = store.getters.user
      const companyData = store.getters.company

      if (!user && !accessToken) {
        // NOT LOGGED IN
        if (router.currentRoute.path !== '/signin') {
          localStorage.removeItem('plausible_ignore')
          stopRefreshingIdToken()
          router.push('/signin').catch(() => {})
        }
      }

      // FIRST TIME HERE
      if (user && !userData) {
        stopRefreshingIdToken()
        router.push('/signup').catch(() => {})
      }

      // Tracking.
      if (userData) {
        refreshIdToken(firebaseApp)

        // Don't track Captego employees.
        if (isCaptegoEmail(user.email)) {
          localStorage.setItem('plausible_ignore', true)
        }

        const userProps = {
          id: userData.uid,
          platform: 'web',
        }
        if (companyData) {
          userProps.companyId = companyData.id
          // userProps.companyTitle = companyData.title
          // if (companyData.logo) userProps.companyLogo = companyData.logo
        }

        Sentry.setUser({ id: userData.uid, companyId: companyData.id })
      }
    })
    .catch((err) => {
      firstAuth = false
      if (err?.code === 'permission-denied') {
        console.log(
          "Looks like you're not an admin user or do not have a company account",
          err
        )
        localStorage.removeItem('plausible_ignore')
        stopRefreshingIdToken()
        deleteTokenCookie()
        firebaseApp.auth().signOut()
        if (router.currentRoute.path !== '/signin') router.push('/signin')
        return
      }
      // Anything else (offline, timeout, backend hiccup) is not proof the
      // session is invalid — keep the user signed in; their refresh token
      // still works and data loads on the next auth event or reload.
      console.log('Failed to load user data, keeping session', err)
      Sentry.captureException(err)
    })
})

router.beforeEach(async (to, from, next) => {
  trackPageview({
    url: redactIDFromURL(to.path, to.params),
  })

  store.dispatch('hideRootNavigation')
  store.dispatch('removeAllPanels')

  if (to.name === 'signout') {
    stopRefreshingIdToken()
    deleteLogoutCookie()
  }

  // Wait for Firebase to restore any persisted session before making auth
  // decisions, so a hard reload isn't mistaken for a signed-out user.
  await initialAuthState

  const accessToken = getAccessToken()
  let currentUser = firebaseApp.auth().currentUser

  // Only sign in with the custom token when there is no live Firebase
  // session, or when the token was issued for another user (handoff from
  // Manage). Custom tokens expire one hour after they are minted, so
  // re-authenticating with a possibly stale one on every navigation would
  // sign the user out even though their session is still valid.
  const tokenUid = accessToken ? decodeJwtPayload(accessToken)?.uid : null
  const needsTokenSignIn =
    accessToken && (!currentUser || (tokenUid && tokenUid !== currentUser.uid))

  if (needsTokenSignIn) {
    try {
      await firebaseApp
        .auth()
        .setPersistence(firebase.auth.Auth.Persistence.LOCAL)
      const response = await firebaseApp
        .auth()
        .signInWithCustomToken(accessToken)
      currentUser = response.user
    } catch (err) {
      console.log('Firebase customToken error', err)
      currentUser = firebaseApp.auth().currentUser
      if (!currentUser) {
        // The token is stale and there is no session to fall back on — the
        // user really is signed out.
        stopRefreshingIdToken()
        setLogoutCookie()

        deleteTokenCookie()
        deleteLogoutCookie()
        await firebaseApp.auth().signOut()

        redirectToManage()
        return
      }
    }
  }

  const isAuthUser = Boolean(currentUser)

  if (getLogoutCookie() === 'manage' && !getTokenCookie()) {
    stopRefreshingIdToken()
    next('/signout')
    return
  }

  // Keep the shared token cookie fresh for cross-app navigation.
  if (
    isAuthUser &&
    to.name !== 'signout' &&
    isTokenNearExpiry(getTokenCookie())
  ) {
    refreshCustomTokenCookie()
  }

  if (isAuthUser && to.name === 'signin') {
    next('/')
    return
  }

  if (!isAuthUser && !getTokenCookie()) {
    stopRefreshingIdToken()
    setLogoutCookie()
    await firebaseApp.auth().signOut()

    redirectToManage()
    return
  }
  next()
})

// Configure Vue to ignore web components.
Vue.config.ignoredElements = ['captego-nav']

const app = new Vue({
  router,
  store,
  i18n,
  render: (h) => h(App),
}).$mount('#app')
